import { Check, Palette } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { palettesByFamily, PALETTES, type PalettePreview } from '@/lib/palettes';
import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

// Colour-palette picker.
//
// Two surfaces, one source of truth:
//   <PalettePicker />  full grid grouped by family, Settings → Appearance
//   <PaletteDots />    one swatch per palette, the user dropdown in TopNav
//
// ── Why the previews are inline styles, not classes ─────────────────────────
// A swatch has to show what a palette looks like *while you are not using it*.
// Tailwind classes resolve against the tokens currently on <html>, so a
// class-driven preview would render every card identically in whatever theme is
// active — useless for choosing. The hexes come from `palettes.ts`, which the
// generator derives from the very same token values the stylesheet emits, so
// these miniatures cannot drift from the real thing.
//
// Previews render in the *currently resolved* mode, so what you see is what you
// get; the light/dark toggle in the header re-renders all of them in the other
// mode, which beats doubling the card count.
//
// ── Show the accent ─────────────────────────────────────────────────────────
// The accent bar in each miniature is not decoration. `--primary` is the token
// that actually differentiates palettes, and the first version of this picker
// left it out of both the preview and the dots — which is exactly why the set
// was reported as looking "very much alike".
//
// ── No Save button, on purpose ──────────────────────────────────────────────
// Choosing a theme is instantly reversible and its result is the thing you are
// looking at. A staged "unsaved changes" flow (as the signature and
// notification sections below it use, correctly — those have consequences)
// would mean picking blind and then confirming.

/** A miniature of the app: page, a card, a couple of text lines, a chip. */
function Miniature({ p, className }: { p: PalettePreview; className?: string }) {
  return (
    <div
      // Generous outer padding on purpose: the page colour is a palette's most
      // recognisable feature, and at p-2 it survived as a 2px ring that read as
      // a border rather than as the page.
      className={cn('overflow-hidden rounded-md p-3', className)}
      style={{ backgroundColor: p.bg }}
      aria-hidden
    >
      <div
        className="rounded-[3px] border p-1.5"
        style={{ backgroundColor: p.surface, borderColor: p.border }}
      >
        <div className="flex items-center gap-1.5">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: p.text }}
          />
          <span
            className="h-[5px] flex-1 rounded-full"
            style={{ backgroundColor: p.text, opacity: 0.85 }}
          />
        </div>
        <div
          className="mt-1.5 h-[4px] w-3/4 rounded-full"
          style={{ backgroundColor: p.muted }}
        />
        <div
          className="mt-1 h-[4px] w-1/2 rounded-full"
          style={{ backgroundColor: p.muted, opacity: 0.6 }}
        />
        <div className="mt-2 flex items-center gap-1">
          {/* The accent button. This is the single most important element in the
              preview: --primary is what actually distinguishes one palette from
              another (buttons, active nav, focus rings, selected rows), and the
              first version of this picker omitted it — which is why eight
              palettes were reported as looking "very much alike". */}
          <span
            className="h-2.5 w-7 rounded-full"
            style={{ backgroundColor: p.accent }}
          />
          <span
            className="h-2.5 w-5 rounded-full"
            style={{ backgroundColor: p.fill }}
          />
          <span
            className="h-2.5 w-3 rounded-full"
            style={{ backgroundColor: p.fill }}
          />
        </div>
      </div>
    </div>
  );
}

export function PalettePicker() {
  const { palette, setPalette, resolved } = useTheme();

  return (
    <section className="rounded-md border border-border/70 bg-background">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div className="min-w-[15rem] flex-1">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold">
            <Palette className="h-3.5 w-3.5 text-muted-foreground" />
            Colour theme
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Applies to your account on this device only — nobody else sees your
            choice. Each theme has a matching light and dark version, so this
            works alongside the light/dark switch rather than replacing it.
            Previews show the {resolved} version, because that's what you're
            using right now.
          </p>
        </div>
        {/* The light/dark switch also lives in the user dropdown, but the two
            controls compose — putting them side by side is what makes that
            legible. Flipping this re-renders every preview below in the other
            mode, which is the fastest way to judge a palette in both. */}
        <div className="w-40 shrink-0">
          <ThemeToggle />
        </div>
      </header>

      {/* A real fieldset + native radios: arrow keys move between options and
          screen readers announce "3 of 22" for free. A div-with-onClick grid
          would need all of that hand-built, and usually ends up without it.
          One fieldset wraps every group so arrow keys traverse the whole set
          rather than trapping you inside a family. */}
      <fieldset className="flex flex-col gap-5 p-4">
        <legend className="sr-only">Colour theme</legend>
        {palettesByFamily().map(({ family, items }) => (
          <div key={family}>
            <h3 className="mb-2 text-3xs font-semibold uppercase tracking-wider text-muted-foreground">
              {family}
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((meta) => {
                const active = palette === meta.key;
                const preview = meta.preview[resolved];
                return (
                  <label
                    key={meta.key}
                    className={cn(
                      'group relative flex cursor-pointer flex-col gap-2 rounded-lg border p-2 transition-colors',
                      'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
                      active
                        ? 'border-foreground/40 bg-accent/50'
                        : 'border-border/70 hover:border-border hover:bg-accent/30',
                    )}
                  >
                    <input
                      type="radio"
                      name="palette"
                      value={meta.key}
                      checked={active}
                      onChange={() => setPalette(meta.key)}
                      className="sr-only"
                    />

                    <div className="relative">
                      <Miniature p={preview} className="h-[74px]" />
                      {/* A two-tone "also has a light/dark version" dot used to
                          sit in the bottom-right corner here. At 14px with a
                          hard 135° colour stop it read as a torn corner rather
                          than a dot — a rendering artifact, not an affordance.
                          The header sentence already makes the point. */}
                      {active ? (
                        <span
                          className="absolute -left-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background"
                          aria-hidden
                        >
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </span>
                      ) : null}
                    </div>

                    <div className="min-w-0">
                      <p
                        className={cn(
                          'truncate text-xs font-medium',
                          active ? 'text-foreground' : 'text-foreground/90',
                        )}
                      >
                        {meta.label}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-3xs leading-snug text-muted-foreground">
                        {meta.blurb}
                      </p>
                      {/* The accessibility claim, stated as a number rather
                          than asserted. Every palette clears WCAG AAA (7:1)
                          for body text — see scripts/gen-themes.mjs, which
                          refuses to emit one that doesn't. */}
                      <p className="mt-1 text-3xs tabular-nums text-muted-foreground/80">
                        {meta.contrast[resolved].toFixed(1)}:1 contrast
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </fieldset>
    </section>
  );
}

/** Compact row of swatches for the user dropdown. Same state, no grid. */
export function PaletteDots({ className }: { className?: string }) {
  const { palette, setPalette, resolved } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn('flex flex-wrap gap-1', className)}
    >
      {PALETTES.map((meta) => {
        const active = palette === meta.key;
        return (
          <button
            key={meta.key}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={meta.label}
            title={`${meta.label} — ${meta.blurb}`}
            onClick={() => setPalette(meta.key)}
            className={cn(
              'h-6 w-6 shrink-0 rounded-full border transition-transform',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-popover',
              // Selection is a ring, not a tick inside the dot. A tick has to
              // pick one colour, and these dots are two-tone — whichever it
              // picked disappeared against the other half.
              active
                ? 'scale-110 border-transparent ring-2 ring-foreground ring-offset-2 ring-offset-popover'
                : 'border-border/70 hover:scale-105 hover:border-foreground/40',
            )}
            // Accent-led, with a wedge of the page colour. Two earlier versions
            // failed here: bg + muted made every palette the same pale blob,
            // and light-page + dark-page made all 22 read as "white over
            // near-black". The accent is the one token that is genuinely unique
            // per palette, so it takes the majority of a 24px dot.
            style={{
              background: `linear-gradient(135deg, ${meta.preview[resolved].accent} 0 62%, ${meta.preview[resolved].bg} 62% 100%)`,
            }}
          />
        );
      })}
    </div>
  );
}
